import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Clock, MessageCircle, ClipboardList, FileBarChart, Trophy, Activity, ShieldCheck, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuyerOpsWidgets() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/buyer/dashboard/ops`, { withCredentials: true })
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <div className="p-6 text-center text-muted-foreground text-sm">Loading operational view…</div>;
  if (!data) return null;

  const { widgets, counts, recent_activity, policy } = data;

  return (
    <section className="mb-8" data-testid="buyer-ops-section">
      {/* Policy hint */}
      <div className={`mb-4 p-3 rounded-lg border ${policy ? 'border-green-300 bg-green-50/30' : 'border-orange/40 bg-orange/5'} flex items-center justify-between gap-3 flex-wrap`} data-testid="ops-policy-strip">
        <div className="flex items-center gap-2 text-sm">
          <ShieldCheck className={`h-4 w-4 ${policy ? 'text-green-600' : 'text-orange'}`} />
          {policy ? (
            <span>Procurement Policy <strong>v{policy.version}</strong> active · Quick Quotation limit ₹{Number(policy.quick_quotation_limit).toLocaleString('en-IN')} · Tender threshold ₹{Number(policy.tender_threshold).toLocaleString('en-IN')}</span>
          ) : (
            <span><strong>No Procurement Policy</strong> on file — pass your committee resolution to enable Quick Quotations.</span>
          )}
        </div>
        <button onClick={() => navigate('/buyer-dashboard/settings/policy')} className="px-3 py-1.5 text-xs font-bold rounded bg-navy text-white inline-flex items-center" data-testid="ops-manage-policy-btn">
          {policy ? 'Manage Policy' : 'Set Up Now'} <ChevronRight className="h-3 w-3 ml-1" />
        </button>
      </div>

      <h2 className="text-base sm:text-lg font-bold text-navy mb-3">Operational View</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <OpCard label="Pending Approvals"  count={counts.pending_approvals}  icon={Clock} color="text-yellow-600" testid="op-pending-approvals" />
        <OpCard label="Clarifications Open" count={counts.clarifications_awaiting} icon={MessageCircle} color="text-navy" testid="op-clarifications" />
        <OpCard label="Quotations Received" count={counts.quotations_received} icon={ClipboardList} color="text-orange" testid="op-quotations" />
        <OpCard label="Comparative Ready"   count={counts.comparative_ready}  icon={FileBarChart} color="text-gold" testid="op-comparative" />
        <OpCard label="Awards Pending"      count={counts.awards_pending}     icon={Trophy} color="text-orange" testid="op-awards" />
        <OpCard label="Active Tenders"      count={counts.active_tenders}     icon={Activity} color="text-green-600" testid="op-active" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-2">
        {/* Pending Approvals list */}
        <Panel title="Pending Approvals" empty="All caught up!" data={widgets.pending_approvals} testid="op-pending-list">
          {widgets.pending_approvals.map(t => (
            <ListRow key={t.id} title={t.title} subtitle={t.status.replace(/_/g, ' ')} onClick={() => navigate(`/tender/${t.id}`)} testid={`op-pending-${t.id}`} />
          ))}
        </Panel>

        {/* Comparative Statements Ready */}
        <Panel title="Comparative Statements Ready" empty="No comparative statements pending." data={widgets.comparative_statements_ready} testid="op-comparative-list">
          {widgets.comparative_statements_ready.map(t => (
            <ListRow key={t.id} title={t.title} subtitle={`${t.bids} quotation${t.bids === 1 ? '' : 's'} received`} onClick={() => navigate(`/tender/${t.id}`)} testid={`op-comparative-${t.id}`} />
          ))}
        </Panel>

        {/* Awards Pending */}
        <Panel title="Awards Pending Decision" empty="No awards pending." data={widgets.awards_pending} testid="op-awards-list">
          {widgets.awards_pending.map(t => (
            <ListRow key={t.id} title={t.title} subtitle="Under Committee Review" onClick={() => navigate(`/tender/${t.id}`)} testid={`op-award-${t.id}`} />
          ))}
        </Panel>

        {/* Recent Activity */}
        <Panel title="Recent Activity Timeline" empty="No recent activity." data={recent_activity} testid="op-activity-list">
          {recent_activity.map(a => (
            <div key={a.id} className="p-2.5 rounded border border-border text-xs" data-testid={`op-activity-${a.id}`}>
              <div className="font-semibold text-navy">{a.action.replace(/[._]/g, ' ')}</div>
              <div className="text-muted-foreground mt-0.5">
                {a.actor_email || 'system'} · {new Date(a.timestamp).toLocaleString()}
              </div>
              {a.policy_version != null && (
                <div className="text-[10px] text-orange mt-0.5">Policy v{a.policy_version}{a.resolution_number ? ` · ${a.resolution_number}` : ''}</div>
              )}
            </div>
          ))}
        </Panel>
      </div>
    </section>
  );
}

const OpCard = ({ label, count, icon: Icon, color, testid }) => (
  <div className="p-3 rounded-lg border border-border bg-card" data-testid={testid}>
    <Icon className={`h-5 w-5 ${color} mb-1.5`} />
    <div className="text-xl font-bold text-navy">{count}</div>
    <div className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{label}</div>
  </div>
);

const Panel = ({ title, empty, data, children, testid }) => (
  <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
    <div className="font-bold text-navy text-sm mb-3">{title}</div>
    {(!data || data.length === 0) ? (
      <div className="text-xs text-muted-foreground italic">{empty}</div>
    ) : (
      <div className="space-y-2">{children}</div>
    )}
  </div>
);

const ListRow = ({ title, subtitle, onClick, testid }) => (
  <button onClick={onClick} className="w-full text-left p-2.5 rounded border border-border hover:border-navy hover:bg-muted/30 transition-colors" data-testid={testid}>
    <div className="text-sm font-semibold text-navy line-clamp-1">{title}</div>
    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{subtitle}</div>
  </button>
);
